import type { Apron, Mode, TicketLeg } from '@trippath/shared';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** How a flight is boarded or left: a jet bridge, stairs down to the apron, or a bus to it. */
export function ApronIcon({ apron }: { apron: Apron }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* After the apron's own: a jet bridge, its walk in sections on legs and wheels, its cab at
          the aircraft; stairs on wheels; the apron's bus, its windows along its side. */}
      {apron === 'bridge' && (
        <>
          <path d="M2 6h15v7H2zM6 6v7M10 6v7M14 6v7M17 6h3l2 2v4l-2 1h-3M5 13v5M14 13v5" />
          <circle cx="5" cy="20" r="1.6" />
          <circle cx="14" cy="20" r="1.6" />
        </>
      )}
      {apron === 'stairs' && (
        <>
          <path d="M2 15 14 4h6M5 17 16 7h4V4M6 13l2 2M9 10l2 2M12 7l2 2M16 7v9M7 16h14" />
          <circle cx="9" cy="19" r="1.6" />
          <circle cx="18" cy="19" r="1.6" />
        </>
      )}
      {apron === 'bus' && (
        <>
          <rect x="2" y="5" width="20" height="11" rx="2" />
          <path d="M2 10h20M7 5v5M12 5v5M17 5v5" />
          <circle cx="7" cy="18.5" r="1.6" />
          <circle cx="17" cy="18.5" r="1.6" />
        </>
      )}
    </svg>
  );
}

/** A way on or off the aircraft by its mark and its word, said in full when pointed at. */
export function ApronMark({ apron, said }: { apron: Apron; said: string }) {
  const { t } = useTranslation();
  return (
    <span className={`apron-mark apron-${apron}`} title={said} aria-label={said} role="img">
      <ApronIcon apron={apron} />
      <span aria-hidden="true">{t(`ticket.apron.${apron}`)}</span>
    </span>
  );
}

/**
 * Where a leg is boarded and where it is left, one end a line: its terminal, its gate and how it
 * is boarded or left; none for an end with nothing said.
 */
export function legEnds(leg: TicketLeg, mode: Mode | 'none', t: TFunction): ReactNode[] {
  const end = (
    side: 'from' | 'to',
    at: string | null,
    gate: string | null,
    apron: Apron | null,
  ): ReactNode => {
    const words = [
      // `1` and `T1` are T1; a terminal by a letter or a name is said so: `Terminal A`.
      at === null
        ? null
        : /^T?\d/.test(at)
          ? `T${at.replace(/^T/, '')}`
          : t('journeys.terminal', { at }),
      gate === null ? null : t(`journeys.gateBy.${mode}`, { gate }),
    ].filter((word) => word !== null);
    if (words.length === 0 && apron === null) return null;
    return (
      <span key={side} className="leg-end">
        {words.join(' · ')}
        {words.length > 0 && apron !== null && ' · '}
        {apron !== null && (
          <ApronMark
            apron={apron}
            said={t(side === 'from' ? `journeys.boardBy.${apron}` : `journeys.offBy.${apron}`)}
          />
        )}
      </span>
    );
  };
  return [
    end('from', leg.terminalFrom, leg.gate, leg.boarding),
    end('to', leg.terminalTo, leg.arrivalGate, leg.deplaning),
  ].filter((line) => line !== null);
}
